import { action, observable, computed } from 'mobx';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import { sortString, sortNumber } from '../utils';
import { NO_DOCUMENTS_MESSAGE, DISALLOWED_FILTER_PARAMS } from '../constants';
import { DocumentSearch } from '../models/configuration/documents/DocumentSearch';
import { DataStore, GlobalDisclaimerStore, TranslationStore } from '.';
import { ShareClassDocument } from '../models/entity';
import { DocumentModuleConfig } from '../models/configuration/DocumentModuleConfig';
import {
    intersect,
    union,
} from '@kurtosys/ksys-app-template/dist/utils/collection';
import {
    isNullOrEmpty,
    isNullOrUndefined,
} from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { translate } from '../Components/Translate';
import getDocumentGroupOrder from '../utils/getDocumentGroupOrder';
import { DataSort } from '../models/configuration/DataSort';
import { ConditionalHelper } from '@kurtosys/ksys-app-template/dist/helpers';
import { IConditional } from '@kurtosys/ksys-app-template/dist/models/common';
import { IQueryOptions } from '@kurtosys/ksys-app-template/dist/models/query';

export class DocumentModuleStore {
    @observable dataStore: DataStore;
    @observable translationStore: TranslationStore;
    @observable documents: Array<ShareClassDocument> = [];
    @observable config: DocumentModuleConfig;
    initialised: boolean = false;

    constructor(dataStore: DataStore) {
        this.dataStore = dataStore;
        this.config = dataStore.config.selectedModule.config;
        this.translationStore = dataStore.stores.translation;

        if (!this.initialised) {
            this.init();
        }
    }

    init = () => {
        this.initialised = true;
        this.getDocuments();
    };

    @computed
    get filterProperties() {
        const dataStore = this.dataStore;
        return dataStore.config.filtersConfig
            .filter(filter => filter.type === this.dataStore.config.entityType)
            .map(filter => filter.property)
            .concat(Object.keys(dataStore.filterParams))
            .filter(property => !DISALLOWED_FILTER_PARAMS.includes(property));
    }

    @action
    getDocuments = async () => {
        const loadingId = this.dataStore.stores.loading.start('global');
        const promises = [];

        if (this.dataStore.watchListSearchEnabled) {
            promises.push(this.getDocumentsFromSearch(this.watchlistSearches));
        }
        if (this.hasIdentifierSearch) {
            promises.push(
                this.getDocumentsFromSearch(this.identifierDocumentSearches)
            );
        }
        if (this.hasFilterSearch) {
            promises.push(
                this.getDocumentsFromSearch([this.filterDocumentSearch])
            );
        }
        if (promises.length === 0) {
            promises.push(this.getDocumentsFromSearch([this.documentSearch]));
        }

        try {
            const results = await Promise.all(promises);
            if (results.length === 1) {
                this.documents = results[0];
            } else {
                let documents: Array<any> = results.shift();
                for (const result of results) {
                    documents = intersect(
                        documents,
                        result,
                        item => item.documentId
                    );
                }
                this.documents = documents;
            }
        } catch (ex) {
            console.warn(ex);
        }
        this.dataStore.stores.loading.stop('global', loadingId);
    };

    @computed
    get watchlistSearches(): Array<DocumentSearch> {
        const { watchListSearch } = this.dataStore;
        const documentSearches = watchListSearch.map(searchItem => {
            const search = [
                {
                    property: searchItem.property,
                    values: searchItem.values,
                    matchtype: searchItem.matchtype,
                },
                ...this.searchFromConfig,
            ];
            return {
                ...this.searchRequest,
                search,
            };
        });
        return documentSearches;
    }

    @computed
    get hasIdentifierSearch() {
        const { identifierSearch } = this.dataStore;
        return identifierSearch && !isNullOrEmpty(identifierSearch.value);
    }

    @computed
    get identifierDocumentSearches(): Array<DocumentSearch> {
        const { identifierSearch } = this.dataStore;
        if (!identifierSearch || isNullOrEmpty(identifierSearch.value)) {
            return [];
        }

        const matchtype = identifierSearch.fuzzy ? 'LIKE' : 'MATCH';
        const values = [identifierSearch.value];

        const documentSearches = identifierSearch.properties.map(property => {
            const search = [
                {
                    property,
                    values,
                    matchtype,
                },
                ...this.searchFromConfig,
            ];
            return {
                ...this.searchRequest,
                search,
            };
        });
        return documentSearches;
    }

    @computed
    get hasFilterSearch() {
        return this.filterSearchItems.length > 0;
    }

    @computed
    get filterSearchItems() {
        const {
            filterSearch = [],
            documentFilterSearch = [],
            documentFilterInferredSearch = [],
        } = this.dataStore;
        const cleanFilterSearch = filterSearch.filter(s =>
            this.filterProperties.includes(s.property)
        );
        const response = [];
        if (cleanFilterSearch && cleanFilterSearch.length > 0) {
            response.push(...cleanFilterSearch);
        }
        response.push(...documentFilterSearch, ...documentFilterInferredSearch);
        return response;
    }

    @computed
    get filterDocumentSearch(): DocumentSearch {
        const search = this.filterSearchItems.map(searchItem => {
            return {
                property: searchItem.property,
                values: searchItem.values,
                matchType: 'MATCH',
            };
        });

        const result = {
            ...this.searchRequest,
            search: [...search, ...this.searchFromConfig],
        };
        return result;
    }

    @computed
    get documentSearch(): DocumentSearch {
        return {
            ...this.searchRequest,
            search: this.searchFromConfig,
        };
    }

    async getDocumentsFromSearch(documentSearches: Array<any>) {
        let documents: Array<any> = [];
        try {
            const promises = documentSearches.map(
                this.dataStore.services.getDocuments
            );
            const responses = await Promise.all<any>(promises);

            for (const response of responses) {
                documents = union(
                    documents,
                    response.values,
                    item => item.documentId
                );
            }
        } catch (ex) {
            console.warn(ex);
        }
        return documents;
    }

    @computed
    get searchFromConfig() {
        const searchConfig = get(this.config, 'search.config.search', []);
        const segmentationSearchConfig = get(
            this.config,
            `search.segmentationSearch.${this.dataStore.fundlist}`,
            []
        );
        const search = searchConfig
            .concat(segmentationSearchConfig.slice())
            .reduce((accumulator, currentValue) => {
                const existingPropertySearch = accumulator.find(
                    a => a.property === currentValue.property
                );

                if (existingPropertySearch) {
                    existingPropertySearch.values = existingPropertySearch.values.concat(
                        currentValue.values
                    );
                    return accumulator;
                }

                accumulator.push(currentValue);

                return accumulator;
            }, []);
        return search;
    }

    @computed
    get searchRequest() {
        return get(this.config, 'search.config', {});
    }

    // Filters documents based on document properties
    // Loop over document property searches and get the value of any matching document property,
    // then check if that value is included in the filter values
    documentPropertyFilter = document => {
        for (const filter of this.dataStore.documentPropertySearch) {
            const documentProperty = get(document, filter.property);
            if (documentProperty) {
                return filter.values.includes(documentProperty);
            }
        }
    };

    @computed
    get documentCategoriesConfig() {
        return this.config.documentCategories;
    }

    @computed
    get hasDocumentCategoriesConfig() {
        return !isNullOrEmpty(this.documentCategoriesConfig);
    }

    @computed
    get documentCategories() {
        if (this.hasDocumentCategoriesConfig) {
            const result = [];
            const conditionalHelper = new ConditionalHelper({ conditions: [] });
            for (const documentCategory of this.documentCategoriesConfig) {
                const documentGroup = this.documents.filter(document => {
                    const conditional =
                        documentCategory.groupBy || ({} as IConditional);
                    return conditionalHelper.matchesWithOptions({
                        instance: document,
                        conditional,
                    });
                });
                const { showSingleDocument = true } = this.config;
                // showSingleDocument for category or default to show showSingleDocument from module config
                const {
                    showSingleDocument: showSingleDocumentForCategory = showSingleDocument,
                } = documentCategory;

                let heading = '';
                let description: string | undefined;
                if (typeof documentCategory.header === 'string') {
                    heading = documentCategory.header;
                } else if (
                    typeof documentCategory.header === 'object' &&
                    documentGroup &&
                    documentGroup.length > 0
                ) {
                    const {
                        property,
                        target = 'meta',
                    } = documentCategory.header;
                    if (target === 'meta') {
                        const metaProperty = documentGroup[0].meta;
                        if (metaProperty) {
                            const headingProperty = metaProperty[property];
                            if (
                                headingProperty &&
                                !isNullOrUndefined(headingProperty.value)
                            ) {
                                heading = headingProperty.value.toString();
                            }
                        }
                    } else if (target === 'inferred') {
                        if (documentGroup[0].inferred) {
                            const inferredValue =
                                documentGroup[0].inferred[property];
                            if (!isNullOrUndefined(inferredValue)) {
                                heading = inferredValue.toString();
                            }
                        }
                    }
                }
                if (documentCategory.descriptionQuery) {
                    description = this.getGlobalQueryValue(
                        documentCategory.descriptionQuery
                    );
                }
                if (documentGroup && !isNullOrEmpty(documentGroup)) {
                    result.push({
                        heading,
                        description,
                        groupedDocuments: this.groupAndSortDocuments(
                            documentGroup
                        ),
                        showSingleDocument: showSingleDocumentForCategory,
                    });
                }
            }
            return result;
        }
        return [];
    }

    getGlobalQueryValue = (queryOptions: IQueryOptions) => {
        const queryStore = this.dataStore.stores.query;
        const executionOptions = queryStore.getExecutionOptionsUsingContext({});
        return queryStore.execute(queryOptions, executionOptions);
    };

    @computed
    get groupedDocuments() {
        return this.groupAndSortDocuments(this.documents);
    }

    @action
    groupAndSortDocuments = documents => {
        const { cultureAliases = {} } = this.config;
        const { documentPropertySearch } = this.dataStore;

        const filteredDocuments =
            documentPropertySearch.length > 0
                ? documents.filter(document =>
                      this.documentPropertyFilter(document)
                  )
                : documents;

        const {
            meta: groupingProperty,
            ungroupedLabel,
            descriptionQueriesByGroupValue,
        } = this.grouping;
        // Tag the ungrouped documents with a grouped label
        for (const filteredDocument of filteredDocuments) {
            const { meta } = filteredDocument;
            // If the grouping property does not exist, add it and set the default if not configured
            if (!meta[groupingProperty]) {
                meta[groupingProperty] = {
                    value: ungroupedLabel || 'Other',
                };
            }
        }

        const groupingField =
            this.grouping.property || `meta.${this.grouping.meta}.value`;

        const groupedDocuments = groupBy(filteredDocuments, groupingField);

        if (
            isNullOrEmpty(this.grouping.order) &&
            !isNullOrEmpty(this.grouping.orderMetaPropertySort)
        ) {
            this.grouping.order = getDocumentGroupOrder(
                groupedDocuments,
                this.grouping.orderMetaPropertySort as DataSort,
                'meta'
            );
        } else if (
            isNullOrEmpty(this.grouping.order) &&
            !isNullOrEmpty(this.grouping.orderInferredPropertySort)
        ) {
            this.grouping.order = getDocumentGroupOrder(
                groupedDocuments,
                this.grouping.orderInferredPropertySort as DataSort,
                'inferred'
            );
        }
        return Object.keys(groupedDocuments)
            .sort(sortString)
            .map(documentGroupName => {
                const documentGroupNameIndex = this.grouping.order.indexOf(
                    documentGroupName
                );
                let description: string | undefined;
                const descriptionQuery =
                    descriptionQueriesByGroupValue &&
                    descriptionQueriesByGroupValue[documentGroupName];

                if (descriptionQuery) {
                    description = this.getGlobalQueryValue(descriptionQuery);
                }
                return {
                    description,
                    order:
                        documentGroupNameIndex > -1
                            ? documentGroupNameIndex
                            : this.grouping.order.length,
                    documentGroupName,
                    documents: groupedDocuments[
                        documentGroupName
                    ].sort((a, b) =>
                        sortString(
                            get(cultureAliases, a.cultureCode, a.cultureCode),
                            get(cultureAliases, b.cultureCode, b.cultureCode)
                        )
                    ),
                };
            })
            .sort((a, b) => sortNumber(a.order, b.order));
    };

    @computed
    get grouping() {
        const { grouping = {} } = this.config;
        const defaults = {
            order: [],
            meta: 'document_type',
        };
        return {
            ...defaults,
            ...grouping,
        };
    }

    @computed
    get header() {
        const { header = {} } = this.config;
        const defaults = {
            label: 'Documents',
        };
        return {
            ...defaults,
            ...header,
        };
    }

    @computed
    get headerDisclaimers(): Array<string> {
        const disclaimerTypes = get(this.config, 'disclaimers.header', []);
        return GlobalDisclaimerStore.getSome(disclaimerTypes);
    }

    @computed
    get footerDisclaimers(): Array<string> {
        const disclaimerTypes = get(this.config, 'disclaimers.footer', []);
        return GlobalDisclaimerStore.getSome(disclaimerTypes);
    }

    @computed
    get noDataDisclaimers(): Array<string> {
        const disclaimerTypes = get(this.config, 'disclaimers.noData', []);
        return disclaimerTypes.length > 0
            ? GlobalDisclaimerStore.getSome(disclaimerTypes)
            : [translate(this.translationStore, NO_DOCUMENTS_MESSAGE)];
    }
}



// WEBPACK FOOTER //
// ./src/stores/DocumentModuleStore.ts